<script setup lang="ts">
// ENG-868: quem abre e fecha o saque agora é o useWithdrawModal — este aviso
// aparece por cima dele (na verdade, no lugar dele) e fechar aqui fecha o
// fluxo inteiro, como fechava o <BaseModal> antes.
const { isOpen: isWithdrawOpen, close: closeModal } = useWithdrawModal();

const { isBonusLossConfirmed, shouldShowBonusLossAlert } =
  useWithdrawBonusLossAlert();

const isOpen = computed(
  () => isWithdrawOpen.value && shouldShowBonusLossAlert.value,
);

const modalCardRef = ref<HTMLElement | null>(null);

const { trapFocus } = useModalA11y(modalCardRef, isOpen, closeModal);

function confirm() {
  isBonusLossConfirmed.value = true;
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="withdraw-bonus-loss-alert-modal fixed inset-0 z-[1000] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
        @dragstart.prevent
      >
        <!-- o clique fora cai NESTE backdrop (cobre o wrapper inteiro) -->
        <div
          class="modal-backdrop absolute inset-0 bg-black/70"
          @click="closeModal"
        />

        <div
          ref="modalCardRef"
          aria-labelledby="withdraw-bonus-loss-alert-title"
          aria-modal="true"
          class="modal-card relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[22px] bg-v2-default-200 text-left lg:max-w-[400px] lg:rounded-[22px]"
          role="dialog"
          tabindex="-1"
          @keydown="trapFocus"
        >
          <UserV2ModalCloseButton @close="closeModal" />

          <!-- rola sozinho, sem o X junto: viewport baixo (ex.: paisagem
               mobile) ou fonte aumentada não corta mais o conteúdo, mesmo
               split do BonusRules.vue -->
          <div
            class="flex min-h-0 flex-col gap-[17px] overflow-y-auto p-[16px]"
          >
            <p
              id="withdraw-bonus-loss-alert-title"
              class="pr-[32px] text-[15px] font-bold leading-[16px] text-white"
            >
              {{ $t('account.wallet.withdraw_modal.bonus_alert_title') }}
            </p>

            <div class="flex flex-col items-center gap-[17px] text-center">
              <p class="text-[16px] leading-[24px]">
                <span class="font-bold text-[#fef08a]">
                  {{
                    $t(
                      'account.wallet.withdraw_modal.bonus_alert_warning_label',
                    )
                  }}
                </span>
                <span class="text-[14px] font-normal leading-[20px] text-white">
                  {{ $t('account.wallet.withdraw_modal.bonus_alert_warning') }}
                </span>
              </p>
              <p class="text-[14px] font-medium leading-[20px] text-[#fef08a]">
                {{
                  $t(
                    'account.wallet.withdraw_modal.bonus_alert_confirm_question',
                  )
                }}
              </p>
            </div>

            <div class="flex w-full gap-[14px]">
              <button
                class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-v2-success px-[10px] text-[14px] font-semibold uppercase text-white"
                type="button"
                @click="confirm"
              >
                {{ $t('account.wallet.withdraw_modal.confirm') }}
              </button>
              <!-- cor exata do Figma (node 24410:70868) — não é v2-danger
                   (#f31260), é um vermelho diferente sem token equivalente
                   em user-v2-tokens.scss -->
              <button
                class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-[#dc2626] px-[10px] text-[14px] font-semibold uppercase text-white"
                type="button"
                @click="closeModal"
              >
                {{ $t('account.wallet.withdraw_modal.cancel') }}
              </button>
            </div>

            <!-- Compliance oficial (ENG-404), mantido igual ao antigo passo
                 dentro do Withdraw.vue -->
            <BaseComplianceInfo />
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação de entrada via
// @keyframes/animation (mesma estratégia do BonusRules.vue).
.modal-backdrop {
  animation: withdraw-bonus-loss-alert-fade 0.18s ease-out;
}

.modal-card {
  animation: withdraw-bonus-loss-alert-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: withdraw-bonus-loss-alert-slide-up;
  }
}

@keyframes withdraw-bonus-loss-alert-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes withdraw-bonus-loss-alert-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes withdraw-bonus-loss-alert-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
